import { useEffect, useState } from "react";
import { safetyApi } from "@/api";
import { AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { Shield, TrendingUp, AlertTriangle, FileText } from "lucide-react";

const PIE_COLORS = ["hsl(0,84%,60%)", "hsl(38,92%,50%)", "hsl(142,70%,45%)", "hsl(215,20%,65%)"];

function HeatmapTable({ data }) {
  const weeks = ["week1", "week2", "week3", "week4"];
  const getColor = (val) => {
    if (val >= 75) return "bg-red-500/80 text-white";
    if (val >= 55) return "bg-orange-500/70 text-white";
    if (val >= 35) return "bg-amber-500/60 text-white";
    return "bg-green-500/50 text-white";
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left px-3 py-2 text-muted-foreground font-bold tracking-widest uppercase">Department</th>
            {weeks.map(w => <th key={w} className="px-3 py-2 text-muted-foreground font-bold tracking-widest uppercase">{w.replace("week", "Wk ")}</th>)}
          </tr>
        </thead>
        <tbody>
          {data.map(row => (
            <tr key={row.dept} className="border-b border-border/50">
              <td className="px-3 py-2 text-foreground font-medium">{row.dept}</td>
              {weeks.map(w => (
                <td key={w} className="px-3 py-2 text-center">
                  <span className={`inline-block px-3 py-1 rounded-sm font-mono font-medium heatmap-cell ${getColor(row[w])}`}>{row[w]}</span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SafetyAnalytics() {
  const [data, setData] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    Promise.all([safetyApi.getAnalytics(), safetyApi.getReports()])
      .then(([a, r]) => { setData(a.data); setReports(r.data); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex-1 p-6 flex items-center justify-center"><div className="text-primary font-mono text-sm animate-pulse">LOADING SAFETY DATA...</div></div>;

  const pieData = [
    { name: "High Risk", value: 23 },
    { name: "Caution", value: 41 },
    { name: "Fit", value: 29 },
    { name: "Unknown", value: 7 },
  ];

  return (
    <div className="p-6 space-y-5 fade-in-up" data-testid="safety-analytics">
      <div>
        <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-1">Module 7</div>
        <h1 className="text-2xl font-black font-heading text-foreground">Safety & Compliance Analytics</h1>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          ["Fatigue Events (YTD)", data?.total_fatigue_events, Shield, "text-red-400"],
          ["Reports Processed", data?.safety_reports_processed, FileText, "text-primary"],
          ["Patterns Detected", data?.patterns_detected, TrendingUp, "text-amber-400"],
          ["High Risk Patterns", data?.high_risk_patterns, AlertTriangle, "text-red-400"],
        ].map(([lbl, val, Icon, col]) => (
          <div key={lbl} className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs tracking-widest uppercase text-muted-foreground font-bold">{lbl}</span>
              <Icon size={14} className={col} />
            </div>
            <div className={`text-3xl font-mono font-medium ${col}`}>{val}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-border">
        {[["overview", "Trend Analysis"], ["heatmap", "Dept Heatmap"], ["fleet", "Fleet Risk"], ["reports", "Safety Reports"]].map(([key, lbl]) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 ${activeTab === key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid={`safety-tab-${key}`}>
            {lbl}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-4">Fatigue Events — 12-Month Trend</div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={data?.trend} margin={{ top: 5, right: 10, bottom: 5, left: -25 }}>
                <defs>
                  <linearGradient id="colorFatigue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(0,84%,60%)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(0,84%,60%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(222,20%,15%)" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: "hsl(215,20%,65%)" }} />
                <YAxis tick={{ fontSize: 10, fill: "hsl(215,20%,65%)" }} />
                <Tooltip contentStyle={{ background: "hsl(222,47%,8%)", border: "1px solid hsl(222,20%,15%)", borderRadius: "2px", fontSize: 11 }} />
                <Area type="monotone" dataKey="fatigue_events" stroke="hsl(0,84%,60%)" fill="url(#colorFatigue)" name="Fatigue Events" strokeWidth={2} />
                <Area type="monotone" dataKey="delays_fatigue_related" stroke="hsl(38,92%,50%)" fill="none" name="Delays" strokeWidth={1.5} strokeDasharray="4 2" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-card border border-border rounded-sm p-4">
            <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-4">Fatigue Risk Distribution</div>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={2} dataKey="value">
                  {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(222,47%,8%)", border: "1px solid hsl(222,20%,15%)", borderRadius: "2px", fontSize: 11 }} />
                <Legend iconSize={8} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {activeTab === "heatmap" && (
        <div className="bg-card border border-border rounded-sm p-4">
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-4">Department Fatigue Risk — Weekly Heatmap</div>
          {data?.dept_heatmap && <HeatmapTable data={data.dept_heatmap} />}
          <div className="flex gap-4 mt-4">
            {[["< 35", "bg-green-500/50", "Low"], ["35–55", "bg-amber-500/60", "Moderate"], ["55–75", "bg-orange-500/70", "High"], ["> 75", "bg-red-500/80", "Critical"]].map(([range, bg, lbl]) => (
              <div key={range} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className={`inline-block w-4 h-4 rounded-sm ${bg}`} />
                {range} · {lbl}
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "fleet" && (
        <div className="bg-card border border-border rounded-sm p-4">
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-4">Fleet Fatigue Risk Comparison</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data?.fleet_risk} margin={{ top: 5, right: 10, bottom: 5, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(222,20%,15%)" />
              <XAxis dataKey="fleet" tick={{ fontSize: 11, fill: "hsl(215,20%,65%)" }} />
              <YAxis tick={{ fontSize: 10, fill: "hsl(215,20%,65%)" }} domain={[0, 100]} />
              <Tooltip contentStyle={{ background: "hsl(222,47%,8%)", border: "1px solid hsl(222,20%,15%)", borderRadius: "2px", fontSize: 11 }} />
              <Bar dataKey="risk_score" name="Risk Score" fill="hsl(217,91%,60%)" radius={[2,2,0,0]} />
              <Bar dataKey="incidents" name="Incidents" fill="hsl(0,84%,60%)" radius={[2,2,0,0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  {["Fleet", "Risk Score", "Incidents", "Crew Count"].map(h => (
                    <th key={h} className="text-left px-3 py-2 text-muted-foreground font-bold tracking-widest uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data?.fleet_risk?.map(f => (
                  <tr key={f.fleet} className="border-b border-border/50">
                    <td className="px-3 py-2 font-mono text-primary">{f.fleet}</td>
                    <td className="px-3 py-2 font-mono">{f.risk_score}</td>
                    <td className="px-3 py-2 font-mono text-red-400">{f.incidents}</td>
                    <td className="px-3 py-2 font-mono">{f.crew_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "reports" && (
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="safety-reports-table">
            <thead>
              <tr className="border-b border-border">
                {["Reference", "Type", "Department", "Risk", "Fatigue Related", "Date", "Status"].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-xs tracking-widest uppercase text-muted-foreground font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reports.map(r => (
                <tr key={r.id} className="border-b border-border/50 row-hover">
                  <td className="px-3 py-2.5 font-mono text-xs text-primary">{r.reference}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{r.type?.replace(/_/g, " ")}</td>
                  <td className="px-3 py-2.5 text-xs text-foreground">{r.department}</td>
                  <td className="px-3 py-2.5">
                    <span className={`text-xs font-bold ${r.risk_level === "high" ? "text-red-400" : r.risk_level === "medium" ? "text-amber-400" : "text-green-400"}`}>{r.risk_level?.toUpperCase()}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`text-xs ${r.fatigue_related ? "text-red-400" : "text-muted-foreground"}`}>{r.fatigue_related ? "Yes" : "No"}</span>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground font-mono">{new Date(r.date).toLocaleDateString()}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground capitalize">{r.status?.replace(/_/g, " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
